import { Injectable } from '@angular/core';
import {
  combineLatest,
  filter,
  firstValueFrom,
  map,
  shareReplay,
  switchMap,
} from 'rxjs';
import { getReadDepartmentIds } from 'server/helpers/permissions/departmentPermissions';
import {
  makeDepartmentPermissionsComplete,
  makePermissionsComplete,
} from 'server/helpers/permissions/permissions';
import { Organisations } from 'server/shared/collections/organisations';
import { Department } from 'server/shared/models/department';
import { Organisation } from 'server/shared/models/organisation';
import { RoleEnum } from 'server/shared/models/role.enum';
import { User } from 'server/shared/models/user';
import {
  filterOrganisationsByDepartmentIds,
  filterOrganisationsByPoolerPermission,
} from 'src/helpers/filterOrganisationsByDepartmentIds';
import { observeTracker } from 'src/helpers/meteorObservable/observeTracker';
import { rxDistinctJSONStringify } from 'src/helpers/rxDistinctJSONStringify';
import { SubscriptionService } from '../shared/subscription.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class OrganisationService {
  readyFullOrganisations$ = this.subscriptionService.organisations.ready$.pipe(
    filter(Boolean),
    switchMap(() => {
      return observeTracker(() => {
        return Organisations.find().fetch();
      });
    }),
    rxDistinctJSONStringify(),
    shareReplay(1),
  );

  readyOrganisations$ = combineLatest([
    this.userService.readyUser$,
    this.readyFullOrganisations$,
  ]).pipe(
    map(([user, organisations]) => {
      const accessibleDepartmentIds = this.getAccessibleDepartmentIds(user);
      if (accessibleDepartmentIds === 'ALL') {
        return organisations;
      }
      let filteredOrganisations = filterOrganisationsByDepartmentIds(
        organisations,
        accessibleDepartmentIds,
      );
      if (user.role === RoleEnum.POOL) {
        filteredOrganisations = filterOrganisationsByPoolerPermission(
          user,
          filteredOrganisations,
        );
      }
      return filteredOrganisations;
    }),
    shareReplay(1),
  );

  getAccessibleDepartmentIds(user: User): string[] | 'ALL' {
    switch (user.role) {
      case RoleEnum.DISPO:
      case RoleEnum.REQUESTER:
        const departmentPermissions =
          user.role === RoleEnum.DISPO
            ? makePermissionsComplete(user.dispoPermissions).departments
            : makeDepartmentPermissionsComplete(user.departmentAccess);
        if (departmentPermissions.readAll) {
          return 'ALL';
        }
        return getReadDepartmentIds(departmentPermissions);
      case RoleEnum.POOL:
        return Object.values(user.customerInformation ?? {}).flatMap((info) => {
          return info.departmentIds ?? [];
        });
    }
  }

  readyDepartments$ = this.readyOrganisations$.pipe(
    map((organisations) => organisations.flatMap((org) => org.departments)),
  );

  constructor(
    private subscriptionService: SubscriptionService,
    private userService: UserService,
  ) {}

  async getOrganisationAndDepartmentById(
    departmentId: string,
  ): Promise<
    { department: Department; organisation: Organisation } | undefined
  > {
    const organisations = await firstValueFrom(this.readyOrganisations$);
    let organisation = organisations.find((organisation) => {
      return organisation.departments.some(
        (department) => department.id == departmentId,
      );
    });
    let department = organisation?.departments.find(
      (department) => department.id == departmentId,
    );
    return organisation && department
      ? { department, organisation }
      : undefined;
  }
}
